import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Home, Users, Phone, ShieldCheck, StickyNote, Clock, IdCard, Trash2, Plus, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";
import { EntityDocuments } from "@/pages/documents/EntityDocuments";

const CARDC = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-amber-100 text-amber-700", lost: "bg-red-100 text-red-700", replaced: "bg-slate-100 text-slate-500" };

export default function FamilyProfile() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { id } = useParams();
  const canManage = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [p, setP] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [ec, setEc] = useState({ name: "", relationship: "", phone: "", phone_alt: "", priority: 1 });
  const [pk, setPk] = useState({ name: "", relationship: "", phone: "", id_document: "" });
  const [noteText, setNoteText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const saveEdit = async (v) => { try { await api.put(`/people/families/${id}`, { name: v.name, email: v.email, phone: v.phone, address: v.address }); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const archiveFamily = async () => { if (!window.confirm(t("archiveConfirm"))) return; try { await api.patch(`/people/families/${id}/archive`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const load = useCallback(() => {
    api.get(`/people/families/${id}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    api.get(`/people/families/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) api.get(`/people/families/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {}); }, [id, canManage]);

  const addEc = async () => {
    try { await api.post(`/people/families/${id}/emergency-contacts`, ec); setEc({ name: "", relationship: "", phone: "", phone_alt: "", priority: 1 }); load(); toast.success(t("save")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delEc = async (cid) => { try { await api.delete(`/people/families/${id}/emergency-contacts/${cid}`); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const addPk = async () => {
    try { await api.post(`/people/families/${id}/authorized-pickups`, pk); setPk({ name: "", relationship: "", phone: "", id_document: "" }); load(); toast.success(t("save")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delPk = async (pid) => { try { await api.delete(`/people/families/${id}/authorized-pickups/${pid}`); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/people/families/${id}/notes`, { text: noteText }); setNoteText(""); api.get(`/people/families/${id}/notes`).then((r) => setNotes(r.data)); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delNote = async (nid) => { try { await api.delete(`/people/families/${id}/notes/${nid}`); api.get(`/people/families/${id}/notes`).then((r) => setNotes(r.data)); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!p) return <div className="text-slate-400">{t("loading")}</div>;
  return (
    <div data-testid="family-profile">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Home className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-slate-900">{p.name}</h1>
            <p className="text-sm text-slate-500">{p.email || "—"} · {p.phone || "—"} · {p.address || "—"}</p>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1" data-testid="family-edit-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" />{t("editFamily")}</Button>
            <Button size="sm" variant="outline" className="gap-1" data-testid="family-archive-btn" onClick={archiveFamily}><Archive className="h-4 w-4" />{t("archive")}</Button>
          </div>
        )}
      </div>
      <EntityDialog open={editOpen} onOpenChange={setEditOpen} title={t("editFamily")} onSubmit={saveEdit}
        initial={{ name: p.name, email: p.email, phone: p.phone, address: p.address }}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") }, { name: "address", label: t("address") }]} />
      <Tabs defaultValue="children">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="children" data-testid="tab-children"><Users className="h-4 w-4 mr-2" />{t("children")}</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-fparents"><Users className="h-4 w-4 mr-2" />{t("parents")}</TabsTrigger>
          <TabsTrigger value="emergency" data-testid="tab-emergency"><Phone className="h-4 w-4 mr-2" />{t("emergencyContacts")}</TabsTrigger>
          <TabsTrigger value="pickups" data-testid="tab-pickups"><ShieldCheck className="h-4 w-4 mr-2" />{t("authorizedPickups")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-fdocuments"><IdCard className="h-4 w-4 mr-2" />{t("documentsTab")}</TabsTrigger>
          {canManage && <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="h-4 w-4 mr-2" />{t("internalNotes")}</TabsTrigger>}
          <TabsTrigger value="timeline" data-testid="tab-ftimeline"><Clock className="h-4 w-4 mr-2" />{t("timeline")}</TabsTrigger>
        </TabsList>

        <TabsContent value="children">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="family-children">
            {p.children.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.children.map((c) => (
                <Card key={c.id} className="border-slate-200 p-4" data-testid="child-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-800">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.level_name || "—"} · {c.current_group?.group_name || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.card_status && <Badge className={`${CARDC[c.card_status] || "bg-slate-100"} border-0`}>{c.card_status}</Badge>}
                      <Link to={`/people/students/${c.id}`}><Button size="sm" variant="outline" className="gap-1"><IdCard className="h-3.5 w-3.5" />{t("viewFile")}</Button></Link>
                    </div>
                  </div>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="parents">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="family-parents">
            {p.parents.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.parents.map((pr) => (
                <Card key={pr.id} className="border-slate-200 p-4">
                  <div className="font-semibold text-slate-800">{pr.name}</div>
                  <div className="text-xs text-slate-500">{pr.email || "—"} · {pr.phone || "—"}</div>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="emergency">
          {canManage && (
            <Card className="border-slate-200 p-4 mb-3" data-testid="emergency-form">
              <div className="grid sm:grid-cols-5 gap-2">
                <Input data-testid="ec-name" placeholder={t("name")} value={ec.name} onChange={(e) => setEc({ ...ec, name: e.target.value })} />
                <Input data-testid="ec-rel" placeholder={t("relationship")} value={ec.relationship} onChange={(e) => setEc({ ...ec, relationship: e.target.value })} />
                <Input data-testid="ec-phone" placeholder={t("phone")} value={ec.phone} onChange={(e) => setEc({ ...ec, phone: e.target.value })} />
                <Input data-testid="ec-phone-alt" placeholder={t("phoneAlt")} value={ec.phone_alt} onChange={(e) => setEc({ ...ec, phone_alt: e.target.value })} />
                <Button data-testid="ec-add-btn" onClick={addEc} className="gap-1"><Plus className="h-4 w-4" />{t("addContact")}</Button>
              </div>
            </Card>
          )}
          <div className="space-y-2" data-testid="emergency-list">
            {p.emergency_contacts.length === 0 ? <p className="text-sm text-slate-400">{t("noContacts")}</p> :
              p.emergency_contacts.map((c) => (
                <Card key={c.id} className="border-slate-200 p-3 flex items-center justify-between" data-testid="emergency-item">
                  <div>
                    <div className="font-medium text-slate-800">{c.name} <span className="text-xs text-slate-400">{c.relationship}</span></div>
                    <div className="text-xs text-slate-500">{c.phone}{c.phone_alt ? ` · ${c.phone_alt}` : ""}</div>
                  </div>
                  {canManage && <Button size="icon" variant="ghost" data-testid={`ec-del-${c.id}`} onClick={() => delEc(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="pickups">
          {canManage && (
            <Card className="border-slate-200 p-4 mb-3" data-testid="pickup-form">
              <div className="grid sm:grid-cols-5 gap-2">
                <Input data-testid="pk-name" placeholder={t("name")} value={pk.name} onChange={(e) => setPk({ ...pk, name: e.target.value })} />
                <Input data-testid="pk-rel" placeholder={t("relationship")} value={pk.relationship} onChange={(e) => setPk({ ...pk, relationship: e.target.value })} />
                <Input data-testid="pk-phone" placeholder={t("phone")} value={pk.phone} onChange={(e) => setPk({ ...pk, phone: e.target.value })} />
                <Input data-testid="pk-id" placeholder={t("idDocument")} value={pk.id_document} onChange={(e) => setPk({ ...pk, id_document: e.target.value })} />
                <Button data-testid="pk-add-btn" onClick={addPk} className="gap-1"><Plus className="h-4 w-4" />{t("addPickup")}</Button>
              </div>
            </Card>
          )}
          <div className="space-y-2" data-testid="pickup-list">
            {p.authorized_pickups.length === 0 ? <p className="text-sm text-slate-400">{t("noPickups")}</p> :
              p.authorized_pickups.map((c) => (
                <Card key={c.id} className="border-slate-200 p-3 flex items-center justify-between" data-testid="pickup-item">
                  <div>
                    <div className="font-medium text-slate-800">{c.name} <span className="text-xs text-slate-400">{c.relationship}</span></div>
                    <div className="text-xs text-slate-500">{c.phone || "—"}{c.id_document ? ` · ${t("idDocument")}: ${c.id_document}` : ""}</div>
                  </div>
                  {canManage && <Button size="icon" variant="ghost" data-testid={`pk-del-${c.id}`} onClick={() => delPk(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </Card>))}
          </div>
        </TabsContent>

        {canManage && (
          <TabsContent value="notes">
            <Card className="border-slate-200 p-4 mb-3">
              <Textarea data-testid="note-input" placeholder={t("noteText")} value={noteText} onChange={(e) => setNoteText(e.target.value)} className="mb-2" />
              <Button data-testid="note-add-btn" onClick={addNote} className="gap-1"><Plus className="h-4 w-4" />{t("addNote")}</Button>
            </Card>
            <div className="space-y-2" data-testid="notes-list">
              {notes.length === 0 ? <p className="text-sm text-slate-400">{t("noNotes")}</p> :
                notes.map((n) => (
                  <Card key={n.id} className="border-slate-200 p-3 flex items-start justify-between" data-testid="note-item">
                    <div>
                      <div className="text-sm text-slate-800">{n.text}</div>
                      <div className="text-xs text-slate-400">{n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <Button size="icon" variant="ghost" data-testid={`note-del-${n.id}`} onClick={() => delNote(n.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </Card>))}
            </div>
          </TabsContent>
        )}

        <TabsContent value="documents">
          <EntityDocuments filterKey="family_id" id={id} testid="family-documents" />
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-2" data-testid="family-timeline">
            {timeline.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              timeline.map((it, i) => (
                <div key={i} className="flex gap-3 items-start rounded-lg border border-slate-100 px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                  <div><div className="text-sm font-medium text-slate-800">{it.title}</div>
                    <div className="text-xs text-slate-500">{(it.date || "").slice(0, 16).replace("T", " ")}{it.detail ? ` · ${it.detail}` : ""}</div></div>
                </div>))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
